/**
 * Context Resource
 *
 * The user-managed context the agent reads every turn: the source registry (`sdk.context.sources`), the per-user profile
 * (`sdk.context.profile`), and the live workspace context read
 * (`sdk.context.workspace`).
 *
 * ROUTE NOTE. `sources.*` maps 1:1 to `/v1/context/sources`. `profile.*` maps to
 * `/v1/user/profile` — the profile is GLOBAL (keyed by user_id alone, no
 * org/account), so it ships in the per-user URL space that matches its actual
 * tenancy, while still being reachable under the `sdk.context` namespace a
 * client thinks of it in. One route, one handler, two names for it here.
 */

import type { HttpLayer } from '../http.js';
import type {
  ContextSource,
  CreateContextSourceRequest,
  ListContextSourcesHealthResponse,
  ListContextSourcesResponse,
  MoveContextSourceRequest,
  UpdateContextSourceRequest,
  UserProfile,
  UserProfilePatch,
  UserProfileView,
  WorkspaceContext,
} from '../types/index.js';

const prefix = '/v1/context';

/** The context-source registry sub-resource. */
export class ContextSourcesResource {
  constructor(private http: HttpLayer) {}

  /**
   * List every source resolvable for the caller — account-scoped, the caller's
   * own user-scoped, and (when `workspaceId` is given) that workspace's — in
   * render order, enabled and disabled alike.
   *
   * Omitting `workspaceId` is the workspace-less lane: workspace-scoped sources
   * are not resolved at all, matching what a workspace-less session's agent sees.
   */
  async list(workspaceId?: string): Promise<ListContextSourcesResponse> {
    return this.http.get(
      `${prefix}/sources`,
      workspaceId ? { workspace_id: workspaceId } : undefined,
    );
  }

  /**
   * Create a source, appended at the end of its scope bucket.
   *
   * Rejections (all before anything is stored): a deferred v2 placement or a
   * seed-only `data_source`/`render_mode` → 422; a `specific-system-prompt`
   * without a `target_persona` resolving to an account persona → 422; a body
   * over 10000 characters → 422; the 500-sources-per-account cap → 422; a
   * duplicate name within the scope → 409.
   */
  async create(params: CreateContextSourceRequest): Promise<ContextSource> {
    return this.http.post(`${prefix}/sources`, params);
  }

  /** Patch a source. Omitted fields are unchanged. */
  async update(
    sourceId: string,
    params: UpdateContextSourceRequest,
  ): Promise<ContextSource> {
    return this.http.patch(`${prefix}/sources/${sourceId}`, params);
  }

  /** Delete a source and re-densify its scope bucket. */
  async delete(sourceId: string): Promise<{ deleted: boolean }> {
    return this.http.delete(`${prefix}/sources/${sourceId}`);
  }

  /**
   * Move a source to a 0-based position within its scope bucket's USER BAND
   * and return the re-densified list.
   */
  async move(
    sourceId: string,
    params: MoveContextSourceRequest,
  ): Promise<ListContextSourcesResponse> {
    return this.http.post(`${prefix}/sources/${sourceId}/move`, params);
  }

  /**
   * Account-scoped latest per-source health from the event-sourced
   * `context_health` store.
   * Never-observed sources are ABSENT — the client derives "not yet observed"
   * from that absence. No assemble / substrate I/O.
   */
  async health(): Promise<ListContextSourcesHealthResponse> {
    return this.http.get(`${prefix}/sources/health`);
  }
}

/**
 * The per-user profile sub-resource — the typed "about me" facts the agent path
 * reads every turn, rendered into the `user-info` block of every system prompt.
 *
 * Always the CALLER'S OWN profile: no user id is accepted anywhere on the route,
 * so another user's profile is not addressable.
 */
export class ContextProfileResource {
  constructor(private http: HttpLayer) {}

  /**
   * Read the caller's stored profile plus the precedence-resolved `effective`
   * view (what the agent is actually told, and which rung each value came from).
   */
  async get(): Promise<UserProfileView> {
    return this.http.get('/v1/user/profile');
  }

  /**
   * Update the caller's OWN profile. Partial: omitted fields are left
   * unchanged, and a field sent as `''` is cleared back to unset.
   */
  async update(patch: UserProfilePatch): Promise<UserProfile> {
    return this.http.put('/v1/user/profile', patch);
  }
}

/**
 * The workspace context sub-resource — what the agent is told about one
 * workspace, read live from the workspace's own substrate (its cloud pod or its
 * assigned workstation). The agent's environment and steering blocks are
 * rendered from this same read.
 */
export class ContextWorkspaceResource {
  constructor(private http: HttpLayer) {}

  /**
   * Read a workspace's agent context: environment facts, the active convention
   * frameworks in precedence order (sot → claude → cursor → codex), the
   * briefing/rule/memory files the agent receives, skipped-file advisories, the
   * skills index, the workspace-scoped context sources, and a `fingerprint`.
   *
   * No client device is needed; the server resolves the workspace's substrate.
   * A sleeping workstation is woken once. A workspace outside the caller's
   * account is a 404.
   *
   * Compare `fingerprint` with the `workspace.contextObserved` bridge
   * notification each agent run publishes (`BridgeEventStream.onWorkspaceContextObserved`
   * on web, the `workspace.contextObserved` event on the desktop `/events`
   * stream) and read again when they differ.
   */
  async get(workspaceId: string): Promise<WorkspaceContext> {
    return this.http.get(`${prefix}/workspaces/${workspaceId}`);
  }
}

export class ContextResource {
  readonly sources: ContextSourcesResource;
  readonly profile: ContextProfileResource;
  readonly workspace: ContextWorkspaceResource;

  constructor(http: HttpLayer) {
    this.sources = new ContextSourcesResource(http);
    this.profile = new ContextProfileResource(http);
    this.workspace = new ContextWorkspaceResource(http);
  }
}
